<template>
  <!-- Only with labels beside: a size container has no width of its own, so a
       checkbox in an auto grid column would shrink to nothing. -->
  <div :class="beside && !inline ? '@container/field' : undefined">
    <FormField v-slot="field" :name="name" :validate-on-input="!!error" :validate-on-model-update="!!error">
      <!-- A checkbox: the box, then its label, with the description and message
           under the label. -->
      <FormItem
        v-if="inline"
        class="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1.5"
        :data-field="name"
      >
        <slot v-if="loading" name="skeleton"><Skeleton class="mt-px size-4 rounded-[4px]" /></slot>
        <FormControl v-else :aria-required="required || undefined">
          <slot v-bind="slotProps(field)" />
        </FormControl>
        <!-- Inline, so a label that wraps carries the icon after its last word
             rather than leaving it alone on a line of its own. -->
        <div class="leading-snug [&>[data-slot=form-label]]:inline">
          <FormLabel class="leading-snug font-normal">
            <span :class="required ? `after:ml-0.5 after:text-danger after:content-['*']` : ''"
              ><slot name="label">{{ label }}</slot></span
            >
          </FormLabel>
          <!-- The word joiner keeps the icon on the line of the label's last word. -->
          <span v-if="tooltip" class="whitespace-nowrap"
            >&#8288;<FieldHelp class="ml-1.5 align-text-bottom" :label="label" :text="tooltip"
          /></span>
        </div>
        <div
          v-if="description || $slots.description || tooltip || $slots.help || field.errorMessage || showWarning"
          class="col-start-2 flex flex-col gap-1.5"
        >
          <!-- The help behind the icon is still the control's description, so a
               screen reader hears it on the control as it did under it. -->
          <FormDescription v-if="hasDescription || tooltip" :class="!hasDescription && 'sr-only'">
            <span v-if="tooltip" class="sr-only">{{ tooltip }}</span>
            <slot name="description">{{ description }}</slot>
          </FormDescription>
          <slot name="help" />
          <FieldWarning v-if="showWarning" :text="warning ?? ''" :joined="false" />
          <FormMessage />
        </div>
      </FormItem>
      <!-- No label column for a hidden label, or the control lands in the 12rem column. -->
      <FormItem
        v-else
        :class="cn('gap-2', beside && !hideLabel && '@lg/field:grid-cols-[12rem_minmax(0,1fr)]')"
        :data-field="name"
      >
        <div
          :class="
            cn(
              'flex flex-wrap items-center gap-x-1.5 gap-y-0.5',
              beside && '@lg/field:min-h-9 @lg/field:items-start',
              hideLabel && 'sr-only',
            )
          "
        >
          <FormLabel :class="beside && '@lg/field:min-h-9 @lg/field:leading-snug'">
            <!-- The marker is generated content, so the label's text stays the
                 field's name ("Password", which the system tests match exactly),
                 and the control says it is required through aria-required. -->
            <span :class="required ? `after:ml-0.5 after:text-danger after:content-['*']` : ''"
              ><slot name="label">{{ label }}</slot></span
            >
          </FormLabel>
          <!-- More than a description holds: what the field is for, on demand. -->
          <FieldHelp v-if="tooltip" :label="label" :text="tooltip" />
        </div>
        <div :class="cn('flex min-w-0 flex-col gap-1.5', centered && 'items-center text-center')">
          <slot v-if="loading" name="skeleton"><Skeleton class="h-9 w-full" /></slot>
          <div
            v-else
            :class="
              cn(
                'flex min-w-0 flex-col',
                (suggestion || showWarning) && '[&_[data-field-frame]]:rounded-b-none [&>:first-child]:rounded-b-none',
                showWarning && '[&>[data-slot=field-suggestion]]:rounded-b-none',
              )
            "
          >
            <slot v-if="ownControl" v-bind="slotProps(field)" />
            <FormControl v-else :aria-required="required || undefined">
              <slot v-bind="slotProps(field)" />
            </FormControl>
            <FieldSuggestion
              v-if="suggest"
              :suggestion="suggestion"
              :link-text="suggest.linkText"
              :invalid="!!field.errorMessage"
              @accept="(value) => accept(value, field.handleChange)"
            />
            <FieldWarning v-if="showWarning" :text="warning ?? ''" />
          </div>
          <FormDescription v-if="hasDescription || tooltip" :class="!hasDescription && 'sr-only'">
            <span v-if="tooltip" class="sr-only">{{ tooltip }}</span>
            <slot name="description">{{ description }}</slot>
          </FormDescription>
          <slot name="help" />
          <FormMessage />
        </div>
      </FormItem>
    </FormField>
  </div>
</template>

<script setup lang="ts">
// Bind the slot's `componentField` onto the native control so [name=…] selectors still work.
import { useFieldError, useFieldValue } from "vee-validate";
import { computed, inject, provide, ref, useSlots } from "vue";

import { useLoading } from "@/components/kit/loadState";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FORM_WARNING } from "@/components/ui/form/injectionKeys";
import { Skeleton } from "@/components/ui/skeleton";
import FieldSuggestion from "@/components/kit/FieldSuggestion.vue";
import { useFieldSuggestion } from "@/composables/useUrlFieldSuggestion";
import { cn } from "@/lib/utils";

import FieldHelp from "./FieldHelp.vue";
import FieldWarning from "./FieldWarning.vue";
import { KIT_FORM_LABELS, nativeName, type FieldSuggest } from "./options";

const props = defineProps<{
  name: string;
  label: string;
  description?: string;
  // Only the marker. Whether the field is required is the schema's business.
  required?: boolean;
  inline?: boolean;
  // What the field is for, behind a help icon beside the label. The
  // description under the control is kept for the value's format and limits.
  tooltip?: string;
  // The label is read out and still names the control (a test's getByLabel
  // finds it), but is not drawn: for a field the page heading already names,
  // such as the one code on a two-factor page.
  hideLabel?: boolean;
  // The control and its message sit in the middle of the row, for a field that
  // is the whole of a narrow page.
  centered?: boolean;
  // A "did you mean" hint read from the field's own value, joined under the
  // control. Not for an inline (checkbox) row.
  suggest?: FieldSuggest;
  // The slot places FormControl itself, on an input nested inside it (a group
  // with a menu beside the input), so the label's id and the aria state land
  // on the input rather than on the group around it.
  ownControl?: boolean;
  // Already translated. Never blocks a submit.
  warning?: string;
}>();

// A field says nothing until it is left (formsConfig.ts), but once it shows an
// error it rechecks on every keystroke, so the message goes as soon as the value
// is right rather than when the admin tabs away.
const error = useFieldError(() => props.name);

const slots = useSlots();
const hasDescription = computed(() => !!props.description || !!slots.description);

const showWarning = computed(() => !!props.warning && !error.value);
provide(FORM_WARNING, showWarning);

const labels = inject(KIT_FORM_LABELS, ref("above"));

// While loading, the control is drawn by the `skeleton` slot.
const loading = useLoading();

const value = useFieldValue<unknown>(() => props.name);
const { suggestion } = useFieldSuggestion(
  computed(() => (props.suggest ? String(value.value ?? "") : "")),
  () => props.suggest?.rules ?? [],
);
// A hint without `accept` only points at the problem; its fix button, when the
// rule has one, does nothing.
function accept(suggested: string, handleChange: (value: unknown) => void) {
  if (props.suggest?.accept) handleChange(suggested);
}

// vee-validate's field, with the native control's name cut to the path's last
// segment (see nativeName) and that name passed for a field that sets it itself.
function slotProps(field: Record<string, any>): Record<string, any> {
  const leaf = nativeName(props.name);
  return { ...field, nativeName: leaf, componentField: { ...field.componentField, name: leaf } };
}
const beside = computed(() => labels.value === "beside");
</script>
